import { Controller } from "@hotwired/stimulus"
import ToastController from "@controllers/toast_controller"

export default class extends Controller {
  static targets = ["emptyState", "generateSection", "generatedContent", "buttonContent", "cancelRegenerateButton", "aiRole", "intent", "personaTags", "status", "fullGuideline", "toggleButton", "progressPanel", "inputActivity", "preview"]
  static values = { activityId: String, generateUrl: String, generatePersonaUrl: String, generateSeedUrl: String, statusUrl: String, saveConfigUrl: String, previewUrl: String }

  connect() {
    // Initialize toast controller
    this.toast = new ToastController()

    // Listen for guideline:generated event from broadcast to reset button
    this.boundHandleGenerated = this.handleGenerated.bind(this)
    window.addEventListener("guideline:generated", this.boundHandleGenerated)

    // Listen for guideline:error event
    this.boundHandleError = this.handleBroadcastError.bind(this)
    window.addEventListener("guideline:error", this.boundHandleError)

    // Listen for persona:content_generated event
    this.boundHandlePersonaGenerated = this.handlePersonaGenerated.bind(this)
    window.addEventListener("persona:content_generated", this.boundHandlePersonaGenerated)

    // Listen for persona:content_error event
    this.boundHandlePersonaError = this.handlePersonaError.bind(this)
    window.addEventListener("persona:content_error", this.boundHandlePersonaError)

    // Listen for streaming progress events
    this.boundHandleStreamingComplete = this.handleStreamingComplete.bind(this)
    this.element.addEventListener("streaming-progress:complete", this.boundHandleStreamingComplete)

    this.boundHandleStreamingError = this.handleStreamingError.bind(this)
    this.element.addEventListener("streaming-progress:error", this.boundHandleStreamingError)

    // Track whether streaming progress is active
    this.isStreaming = false

    // Polling interval (in milliseconds)
    this.pollingInterval = null

    // Store original button content on connect
    if (this.hasButtonContentTarget) {
      this.originalContent = this.buttonContentTargets[0]?.innerHTML
    }

    // Check if there's already a job running (in case of page refresh)
    this.checkInitialJobStatus()
  }

  disconnect() {
    window.removeEventListener("guideline:generated", this.boundHandleGenerated)
    window.removeEventListener("guideline:error", this.boundHandleError)
    window.removeEventListener("persona:content_generated", this.boundHandlePersonaGenerated)
    window.removeEventListener("persona:content_error", this.boundHandlePersonaError)
    this.element.removeEventListener("streaming-progress:complete", this.boundHandleStreamingComplete)
    this.element.removeEventListener("streaming-progress:error", this.boundHandleStreamingError)
    this.stopPolling()
  }

  handlePersonaGenerated(event) {
    // Reset button when persona content is generated via broadcast
    if (event.detail?.activity_id === this.activityIdValue) {
      this.stopPolling()
      this.resetButton()
      // Reload page to show updated persona content
      window.location.reload()
    }
  }

  handlePersonaError(event) {
    // Handle persona generation error broadcast
    if (event.detail?.activity_id === this.activityIdValue) {
      this.stopPolling()
      this.showError(event.detail?.error || 'Failed to generate persona content')
      this.resetButton()
    }
  }

  async handleGenerated(event) {
    // Reset button when guideline is generated via broadcast
    if (event.detail?.activity_id === this.activityIdValue) {
      this.stopPolling()
      this.resetButton()
      // If streaming progress is active, let it handle the content refresh
      if (!this.isStreaming) {
        await this.refreshGuidelineContent()
      }
    }
  }

  handleBroadcastError(event) {
    // Handle error broadcast
    if (event.detail?.activity_id === this.activityIdValue) {
      this.stopPolling()
      this.showError(event.detail?.error || 'Failed to generate guideline')
      this.resetButton()
    }
  }

  startPolling() {
    // Poll every 5 seconds to check job status
    this.pollingInterval = setInterval(() => {
      this.checkJobStatus()
    }, 5000)
  }

  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval)
      this.pollingInterval = null
    }
  }

  async checkJobStatus() {
    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'completed') {
          // Job finished successfully - stop polling
          // The Turbo Stream broadcast will trigger page reload via event listener
          this.stopPolling()
        } else if (data.status === 'idle') {
          // No job running (might have been cleared)
          this.stopPolling()
          this.resetButton()
        } else if (data.status === 'failed') {
          // Job failed in Solid Queue
          this.stopPolling()
          this.showError(data.error || 'Failed to generate guideline')
          this.resetButton()
        } else if (data.status === 'error') {
          // Job failed with error
          this.stopPolling()
          this.showError(data.message || 'Failed to generate guideline')
          this.resetButton()
        }
        // If status is 'processing', keep polling
      } else {
        console.error('Failed to check job status:', response.status)
      }
    } catch (error) {
      console.error('Error checking job status:', error)
      // Don't stop polling on network errors, might be temporary
    }
  }

  async checkInitialJobStatus() {
    // Check if there's a job already running when page loads
    if (!this.hasStatusUrlValue) return

    try {
      const response = await fetch(this.statusUrlValue, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()

        if (data.status === 'processing') {
          // Job is running - reconnect to stream if URL available, otherwise poll
          if (data.stream_url) {
            this.showLoading()
            this.startStreamingProgress(data.stream_url)
          } else {
            this.showProcessingState()
            this.startPolling()
          }
        }
      }
    } catch (error) {
      console.error('Error checking initial job status:', error)
      // Silently fail - not critical for initial load
    }
  }

  async generate(event) {
    event?.preventDefault()

    // Get form data
    const aiRole = this.hasAiRoleTarget ? this.aiRoleTarget.value : 'expert_interviewer'
    const intent = this.hasIntentTarget ? this.intentTarget.value : ''
    const personas = this.getPersonas()

    // Validate
    if (!intent.trim()) {
      this.showError('Please provide an intent for the conversation')
      return
    }

    if (personas.length === 0) {
      this.showError('Please add at least one persona')
      return
    }

    // Show loading state
    this.showLoading()

    try {
      const response = await fetch(this.generateUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ai_role: aiRole,
          intent: intent,
          personas: personas,
          input_activity_ids: this.getInputActivityIds()
        })
      })

      if (response.ok) {
        const data = await response.json()

        // If streaming progress is available, use it instead of polling
        if (data.stream_url) {
          this.startStreamingProgress(data.stream_url)
        } else {
          // Fallback to polling
          this.showProcessingState()
          this.startPolling()
        }
      } else {
        const error = await response.json()
        this.showError(error.error || 'Failed to generate guideline')
        this.resetButton()
      }
    } catch (error) {
      console.error('Guideline generation error:', error)
      this.showError('An error occurred. Please try again.')
      this.resetButton()
    }
  }

  getPersonas() {
    if (this.hasPersonaTagsTarget) {
      const tags = this.personaTagsTarget.querySelectorAll('[data-tag-value]')
      const input = this.personaTagsTarget.closest('[data-controller~="tag-input-guideline"]')?.querySelector('[data-tag-input-guideline-target="input"]')
      const personas = [...Array.from(tags).map(tag => tag.dataset.tagValue), ...(input?.value.split(',') || [])]
        .map(value => value.trim()).filter(Boolean)
      return personas.filter((value, index) => personas.findIndex(other => other.toLowerCase() === value.toLowerCase()) === index)
    }
    return []
  }

  getInputActivityIds() {
    if (this.hasInputActivityTarget) {
      return this.inputActivityTargets
        .filter(checkbox => checkbox.checked)
        .map(checkbox => checkbox.value)
    }
    return []
  }

  async saveInputActivities() {
    if (!this.hasSaveConfigUrlValue) return

    try {
      await fetch(this.saveConfigUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          guideline_input_activity_ids: this.getInputActivityIds()
        })
      })
    } catch (error) {
      console.error('[guideline-generator] Failed to save input activities:', error)
    }
  }

  showLoading() {
    // Update all button content targets to show loading and disable buttons
    this.buttonContentTargets.forEach(target => {
      if (!this.originalContent) {
        this.originalContent = target.innerHTML
      }
      // Find the parent button element
      const button = target.closest('button')
      if (button) {
        button.disabled = true
      }
      target.innerHTML = `
        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Generating...</span>
      `
    })
  }

  showProcessingState() {
    // Update all button content targets to show processing and disable buttons
    this.buttonContentTargets.forEach(target => {
      // Find the parent button element
      const button = target.closest('button')
      if (button) {
        button.disabled = true
      }
      target.innerHTML = `
        <svg class="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Processing...</span>
      `
    })
  }

  resetButton() {
    // Restore original button content and re-enable buttons
    if (this.originalContent) {
      this.buttonContentTargets.forEach(target => {
        // Find the parent button element
        const button = target.closest('button')
        if (button) {
          button.disabled = false
        }
        target.innerHTML = this.originalContent
      })
    }
  }

  showError(message) {
    // Use toast notification for errors
    this.toast.error(message)
  }

  regenerate() {
    // Show generate section above generated content
    this.generateSectionTarget.classList.remove("hidden")

    if (this.hasCancelRegenerateButtonTarget) this.cancelRegenerateButtonTarget.classList.remove("hidden")

    // Scroll to the generate section
    this.generateSectionTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  cancelRegenerate() {
    // Hide generate section
    this.generateSectionTarget.classList.add("hidden")

    if (this.hasCancelRegenerateButtonTarget) this.cancelRegenerateButtonTarget.classList.add("hidden")

    // Scroll back to generated content
    this.generatedContentTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  async generatePersonaContent(event) {
    event?.preventDefault()

    if (!this.hasGeneratePersonaUrlValue) {
      this.showError("Generate persona URL not configured")
      return
    }

    const button = event?.currentTarget
    const personaName = button?.dataset?.persona

    if (!personaName) {
      this.showError("Persona name not found")
      return
    }

    // Show loading state on the button
    const originalContent = button?.innerHTML
    if (button) {
      button.disabled = true
      button.innerHTML = `
        <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        <span>Generating...</span>
      `
    }

    try {
      const response = await fetch(this.generatePersonaUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          persona_name: personaName
        })
      })

      if (response.ok) {
        // Job queued successfully, show processing state and start polling
        this.showProcessingState()
        this.startPolling()
      } else {
        const error = await response.json()
        this.showError(error.error || 'Failed to generate persona content')
        if (button) {
          button.disabled = false
          button.innerHTML = originalContent
        }
      }
    } catch (error) {
      console.error('Persona content generation error:', error)
      this.showError('An error occurred. Please try again.')
      if (button) {
        button.disabled = false
        button.innerHTML = originalContent
      }
    }
  }

  async test(event) {
    if (this.hasPreviewUrlValue) return this.previewGroup(event)
    // Get the activity ID and construct the test conversation URL
    const activityId = this.activityIdValue
    const button = event.currentTarget

    // Get inloop ID from the URL
    const urlParts = window.location.pathname.split('/')
    const inloopIndex = urlParts.indexOf('facilitator') + 2 // Get index after 'facilitator/inloops'
    const inloopId = urlParts[inloopIndex]

    // Disable button and show loading state
    button.disabled = true
    const originalContent = button.innerHTML
    button.innerHTML = `
      <span class="flex items-center gap-2">
        <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
        </svg>
        Starting test...
      </span>
    `

    try {
      const response = await fetch(`/facilitator/inloops/${inloopId}/activities/${activityId}/start_test_conversation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        // Redirect to the test conversation
        window.location.href = data.url
      } else {
        const error = await response.json()
        this.showError(error.error || 'Failed to start test conversation')
        button.disabled = false
        button.innerHTML = originalContent
      }
    } catch (error) {
      console.error('Test conversation error:', error)
      this.showError('An error occurred. Please try again.')
      button.disabled = false
      button.innerHTML = originalContent
    }
  }

  async saveDraft(event) {
    const intent = this.intentTarget.value.trim()
    if (!intent) {
      this.showError('Please provide an intent for the conversation')
      return
    }
    const button = event.currentTarget
    button.disabled = true
    try {
      const response = await fetch(this.saveConfigUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ guideline_data: {
          intent, ai_role_key: this.aiRoleTarget.value,
          personas: this.getPersonas(),
          ...(!this.hasGeneratedContentTarget ? { content: { general_approach: intent } } : {}),
          input_activity_ids: this.getInputActivityIds()
        } })
      })
      if (!response.ok) throw new Error((await response.json()).error || 'Failed to save guideline')
      await this.refreshGuidelineContent()
    } catch (error) {
      this.showError(error.message)
    } finally {
      button.disabled = false
    }
  }

  async previewGroup(event) {
    const button = event.currentTarget
    button.disabled = true
    try {
      const response = await fetch(this.previewUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })
      const data = await response.json()
      if (!response.ok) throw new Error(data.error || 'Failed to test guideline')
      this.previewTarget.textContent = [data.opening, data.response].filter(Boolean).join('\n\n')
      this.previewTarget.classList.add('whitespace-pre-wrap')
      this.previewTarget.hidden = false
      this.previewTarget.classList.remove('hidden')
      this.previewTarget.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' })
    } catch (error) {
      this.showError(error.message)
    } finally {
      button.disabled = false
    }
  }

  async generateSeed(event) {
    if (!this.hasGenerateSeedUrlValue) {
      this.showError("Seed generation is not configured")
      return
    }

    const button = event?.currentTarget
    const originalContent = button?.innerHTML
    if (button) {
      button.disabled = true
      button.innerHTML = `
        <span class="flex items-center gap-2">
          <svg class="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M8.54661 19.7675C10.9457 20.8319 13.8032 20.7741 16.2502 19.3613C20.3157 17.0141 21.7086 11.8156 19.3614 7.75008L19.1114 7.31706M4.63851 16.2502C2.2913 12.1847 3.68424 6.98619 7.74972 4.63898C10.1967 3.22621 13.0542 3.16841 15.4533 4.23277M2.49341 16.3338L5.22546 17.0659L5.95751 14.3338M18.0426 9.6659L18.7747 6.93385L21.5067 7.6659"/>
          </svg>
          Regenerating...
        </span>
      `
    }

    try {
      const response = await fetch(this.generateSeedUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]")?.content,
          "Accept": "application/json"
        }
      })

      if (response.ok) {
        window.location.reload()
      } else {
        const error = await response.json()
        this.showError(error.error || "Failed to generate seed")
      }
    } catch (error) {
      console.error("Seed generation error:", error)
      this.showError("An error occurred. Please try again.")
    } finally {
      if (button) {
        button.disabled = false
        button.innerHTML = originalContent
      }
    }
  }

  startStreamingProgress(streamUrl) {
    this.isStreaming = true

    // Show progress panel, hide all other sections
    if (this.hasProgressPanelTarget) {
      this.progressPanelTarget.classList.remove("hidden")
    }
    if (this.hasGenerateSectionTarget) {
      this.generateSectionTarget.classList.add("hidden")
    }
    if (this.hasEmptyStateTarget) {
      this.emptyStateTarget.classList.add("hidden")
    }
    if (this.hasGeneratedContentTarget) {
      this.generatedContentTarget.classList.add("opacity-50", "pointer-events-none")
    }

    // Find the streaming-progress controller and start it
    // Use a small delay to ensure Stimulus has connected the child controller on page load
    const tryConnect = () => {
      const progressEl = this.element.querySelector('[data-controller="streaming-progress"]')
      if (progressEl) {
        const progressController = this.application.getControllerForElementAndIdentifier(progressEl, "streaming-progress")
        if (progressController) {
          progressController.startStreaming(streamUrl, { initialMessage: "Generating conversation guidelines..." })
          return true
        }
      }
      return false
    }

    if (tryConnect()) return

    // Retry after a frame in case Stimulus hasn't connected yet (page load race condition)
    requestAnimationFrame(() => {
      if (tryConnect()) return

      // Final retry with a short delay
      setTimeout(() => {
        if (tryConnect()) return

        // If still not found, fall back to polling
        console.warn("[guideline-generator] streaming-progress controller not found, falling back to polling")
        this.isStreaming = false
        if (this.hasProgressPanelTarget) {
          this.progressPanelTarget.classList.add("hidden")
        }
        if (this.hasGeneratedContentTarget) {
          this.generatedContentTarget.classList.remove("opacity-50", "pointer-events-none")
        } else if (this.hasEmptyStateTarget) {
          this.emptyStateTarget.classList.remove("hidden")
        }
        this.showProcessingState()
        this.startPolling()
      }, 100)
    })
  }

  async handleStreamingComplete(event) {
    // Keep isStreaming true to prevent handleGenerated broadcast from triggering reload
    this.resetButton()

    // Wait for progress bar to visually reach 100% before refreshing content
    await new Promise(resolve => setTimeout(resolve, 1500))

    if (this.hasProgressPanelTarget) {
      this.progressPanelTarget.classList.add("hidden")
    }
    if (this.hasGeneratedContentTarget) {
      this.generatedContentTarget.classList.remove("opacity-50", "pointer-events-none")
    }

    // Refresh guideline content in-place without full page reload
    await this.refreshGuidelineContent()
    this.isStreaming = false
  }

  async refreshGuidelineContent() {
    try {
      const response = await fetch(window.location.href, {
        headers: { 'Accept': 'text/html' }
      })
      if (!response.ok) throw new Error('Failed to fetch')

      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const containerId = `guideline-container-${this.activityIdValue}`
      const newContainer = doc.getElementById(containerId)

      if (newContainer) {
        const currentContainer = document.getElementById(containerId)
        if (currentContainer) {
          currentContainer.outerHTML = newContainer.outerHTML
        }
      }
    } catch (error) {
      console.error('[guideline-generator] Failed to refresh content:', error)
      window.location.reload()
    }
  }

  handleStreamingError(event) {
    this.isStreaming = false
    this.showError(event.detail?.message || 'Failed to generate guideline')
    this.resetButton()

    // Hide progress panel and restore previous content after a delay
    setTimeout(() => {
      if (this.hasProgressPanelTarget) {
        this.progressPanelTarget.classList.add("hidden")
      }
      if (this.hasGeneratedContentTarget) {
        this.generatedContentTarget.classList.remove("opacity-50", "pointer-events-none")
      } else if (this.hasEmptyStateTarget) {
        this.emptyStateTarget.classList.remove("hidden")
      }
    }, 3000)
  }

  editSection(event) {
    // Handle edit section
    const section = event.currentTarget.closest("[data-section]")
  }

  shuffleSection(event) {
    // Handle shuffle section
    const section = event.currentTarget.closest("[data-section]")
  }

  deleteSection(event) {
    // Handle delete section
    const section = event.currentTarget.closest("[data-section]")
    if (confirm("Are you sure you want to delete this section?")) {
      section.remove()
    }
  }

  toggleFullGuideline() {
    if (this.hasFullGuidelineTarget) {
      const isHidden = this.fullGuidelineTarget.classList.contains("hidden")
      this.fullGuidelineTarget.classList.toggle("hidden")

      if (this.hasToggleButtonTarget) {
        if (isHidden) {
          this.toggleButtonTarget.innerHTML = `
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M18 15L12 9L6 15"/>
            </svg>
            Hide full guideline
          `
        } else {
          this.toggleButtonTarget.innerHTML = `
            <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 9L12 15L18 9"/>
            </svg>
            Show full guideline
          `
        }
      }
    }
  }

  editGreeting() {
  }

  addPersona() {
  }

  switchPersona(event) {
    // Remove active class from all persona tabs
    const allTabs = this.element.querySelectorAll("[data-persona-tab]")
    allTabs.forEach(tab => {
      tab.classList.remove("bg-[#FAFAFB]", "text-[#1F2937]")
      tab.classList.add("text-[#6B7280]")
    })

    // Add active class to clicked tab
    const clickedTab = event.currentTarget
    clickedTab.classList.add("bg-[#FAFAFB]", "text-[#1F2937]")
    clickedTab.classList.remove("text-[#6B7280]")

    // Switch content based on persona
  }
}
